import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { MaiaUploadService } from "../../data-access-layer/maia/maia-upload.service";

/** A paid dataset offered on the enrichment step (checkbox card). */
interface EnrichDataset {
    id: string;
    name: string;
    description: string;
    scope: string;
    scopeType: "global" | "country";
    icon: "shield" | "building" | "flame";
    iconColor: string;
    selected: boolean;
}

/** Display styling per known overlay id; the list of which overlays to show comes from the account. */
interface OverlayMeta {
    description: string;
    scope: string;
    scopeType: "global" | "country";
    icon: "shield" | "building" | "flame";
    iconColor: string;
}

/**
 * MAIA wizard — Step 3 of 4 (Portfolio enrichment).
 *
 * Select paid overlay datasets (Munich Re, Precisely COPE, Precisely Wildfire) to enrich the
 * portfolio; core MAPTYCS data is free. Sits between geocoding and the grouping/approval step.
 * Datasets are a data array so they can later come from the paid-overlay registry API.
 */
@Component({
    selector: "maia-enrich-step",
    templateUrl: "./maia-enrich-step.component.html",
    styleUrls: ["./maia-enrich-step.component.scss"],
})
export class MaiaEnrichStepComponent implements OnInit {
    @Input() datasetId: string | null = null;
    @Input() jobId: number | null = null;

    @Output() readonly closeRequested = new EventEmitter<void>();
    @Output() readonly backRequested = new EventEmitter<void>();
    @Output() readonly submitted = new EventEmitter<string[]>();

    // Display styling by overlay id; the actual list shown is the account's assigned overlays.
    private static readonly META: { [id: string]: OverlayMeta } = {
        munichre_hazards: {
            description: "Natural hazard risk scores and loss estimates",
            scope: "Global", scopeType: "global", icon: "shield", iconColor: "#12478f",
        },
        precisely_building: {
            description: "Property characteristics and building attributes",
            scope: "USA", scopeType: "country", icon: "building", iconColor: "#12478f",
        },
        precisely_wildfire_risk: {
            description: "Wildfire risk scoring and exposure data",
            scope: "USA", scopeType: "country", icon: "flame", iconColor: "#0891b2",
        },
        precisely_crime: {
            description: "Crime index scores by location",
            scope: "USA", scopeType: "country", icon: "shield", iconColor: "#b91c1c",
        },
    };

    // Populated from the account's assigned paid overlays (FR-1.29 / FR-1.49).
    datasets: EnrichDataset[] = [];
    loading = false;

    /** Confirmation popup ("Add Premium Datasets?") shown before charging. */
    showConfirm = false;
    submitting = false;

    constructor(private readonly maiaUploadService: MaiaUploadService) {}

    ngOnInit(): void {
        if (!this.datasetId) {
            return;
        }
        this.loading = true;
        this.maiaUploadService.getPaidOverlays(this.datasetId).subscribe(
            (overlays) => {
                this.datasets = (overlays || []).map((overlay) => {
                    const meta = MaiaEnrichStepComponent.META[overlay.id] || {
                        description: overlay.provider, scope: "", scopeType: "global" as const,
                        icon: "shield" as const, iconColor: "#12478f",
                    };
                    return {
                        id: overlay.id,
                        name: overlay.name,
                        description: meta.description,
                        scope: meta.scope,
                        scopeType: meta.scopeType,
                        icon: meta.icon,
                        iconColor: meta.iconColor,
                        selected: false,
                    };
                });
                this.loading = false;
            },
            () => {
                this.loading = false;
            }
        );
    }

    toggle(dataset: EnrichDataset): void {
        dataset.selected = !dataset.selected;
    }

    get selectedIds(): string[] {
        return this.datasets.filter((dataset) => dataset.selected).map((dataset) => dataset.id);
    }

    /** Start Enrichment: confirm the charge when datasets are selected; skip straight through if none. */
    startEnrichment(): void {
        if (this.selectedIds.length > 0) {
            this.showConfirm = true;
            return;
        }
        this.submitted.emit([]);
    }

    cancelConfirm(): void {
        this.showConfirm = false;
    }

    confirmAdd(): void {
        const overlayIds = this.selectedIds;
        // No job (e.g. deep-link testing) -> can't trigger the stage; just advance.
        if (!this.datasetId || this.jobId == null) {
            this.showConfirm = false;
            this.submitted.emit(overlayIds);
            return;
        }
        this.submitting = true;
        this.maiaUploadService.triggerPaidOverlay(this.datasetId, this.jobId, overlayIds).subscribe(
            () => {
                this.submitting = false;
                this.showConfirm = false;
                this.submitted.emit(overlayIds);
            },
            () => {
                this.submitting = false;
            }
        );
    }
}
